import { useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { useAuth } from '@/app/auth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { SortArrow } from '@/components/ui/SortArrow';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { cx } from '@/lib/cx';
import { icaoOf } from '@/types/airline';
import type { Airline } from '@/types/airline';
import { ROLES_DICTIONARY_WRITE } from '@/types/user';
import { AirlinePanel } from './AirlinePanel';
import { ROWS_OPTIONS, useAirlineParams } from './useAirlineParams';
import type { AirlineSortField } from './useAirlineParams';
import { useAirlineDictionary } from './useAirlineDictionary';
import styles from './AirlinesPage.module.css';

/**
 * The airline dictionary.
 *
 * Readable by `ADMIN`, `AAK`, `USER` and `KGA_USER`; writable by `ADMIN` alone, so the
 * add button and the panel's actions are gated on the role while the table and the
 * card are not. That gating is a courtesy — the server refuses a write from any other
 * role with a 403 regardless — but a control that can only answer 403 wastes the
 * reader's time.
 *
 * **Everything here narrows an array, not a query.** `GET /airline/list` takes no
 * filter, sort or page: it hands back all ~1035 carriers in one response, so the
 * search, the order and the pager all work over what is already in the browser.
 *
 * The pager is here and NOT on the user list for one reason: scale. A thousand rows
 * of DOM is a thousand rows re-rendered on every keystroke of the search box, while a
 * few dozen users are cheaper to render than to page through.
 */
export const AirlinesPage = () => {
  const { params, update, select, reset, hasFilters } = useAirlineParams();
  const { airlines, isLoading, errorKey, reload, apply, remove } = useAirlineDictionary();
  const { can } = useAuth();
  const { t } = useTranslation();

  /** Always true today — the route is ADMIN-only. See the note in AirportsPage. */
  const canWrite = can(ROLES_DICTIONARY_WRITE);

  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    if (flash === null) return undefined;

    const timer = window.setTimeout(() => setFlash(null), 6000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const matched = useMemo(() => {
    const needle = params.query.trim().toLowerCase();

    const filtered = airlines.filter((airline) => {
      if (params.working !== '' && String(airline.kzFlying ?? false) !== params.working) {
        return false;
      }
      return needle === '' || matches(airline, needle);
    });

    // Sorted on a copy: `airlines` belongs to the hook, and sorting it in place would
    // reorder the list the panel is reading from mid-render.
    return [...filtered].sort((left, right) => {
      const order = compare(left, right, params.sort);
      return params.dir === 'ascending' ? order : -order;
    });
  }, [airlines, params.query, params.working, params.sort, params.dir]);

  /**
   * The page in view.
   *
   * Clamped rather than trusted: a filter that shrinks the result set can leave the
   * URL asking for page 9 of 2, and slicing past the end would show an empty table
   * beside a count saying there are matches.
   */
  const pageCount = Math.max(1, Math.ceil(matched.length / params.rows));
  const page = Math.min(params.page, pageCount);
  const rows = matched.slice((page - 1) * params.rows, page * params.rows);

  const { selection } = params;

  // Looked up in the WHOLE list rather than the page in view, so paging past an open
  // row does not close its card.
  const selected =
    selection.mode === 'edit'
      ? (airlines.find((airline) => airline.id === selection.id) ?? null)
      : null;

  const missing = selection.mode === 'edit' && selected === null && !isLoading && !errorKey;

  // Creating and editing open the same dialog; only editing has a row to show.
  const isCreating = selection.mode === 'create';

  const handleSaved = (airline: Airline, action: 'created' | 'saved') => {
    apply(airline);
    setFlash(t(`airlines.flash.${action}`, { code: airline.iataCode ?? String(airline.id) }));

    // The dialog closes, and the message above the table is what confirms the save —
    // inside the dialog it would sit behind the scrim.
    select({ mode: 'closed' });
  };

  const handleDeleted = (airline: Airline) => {
    remove(airline.id);
    setFlash(t('airlines.flash.deleted', { code: airline.iataCode ?? String(airline.id) }));
    select({ mode: 'closed' });
  };

  return (
    <>
      <div className={styles.page}>
        <div className={styles.toolbar}>
          <h1 className={styles.title}>{t('airlines.title')}</h1>

          <div className={styles.spacer} />

          <Button size="sm" onClick={reload} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
          {canWrite && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => select({ mode: 'create' })}
              disabled={selection.mode === 'create'}
            >
              {t('airlines.add')}
            </Button>
          )}
        </div>

        <div className={styles.filters}>
          <SearchInput
            label={t('common.search')}
            placeholder={t('airlines.searchPlaceholder')}
            value={params.query}
            onChange={(query) => update({ query })}
            width="lg"
          />

          <label className={styles.filter}>
            <span className={styles.filterLabel}>{t('airlines.columns.working')}</span>
            <Select
              options={[
                { value: 'true', label: t('airlines.states.working') },
                { value: 'false', label: t('airlines.states.notWorking') },
              ]}
              placeholder={t('airlines.allStates')}
              value={params.working}
              active={params.working !== ''}
              onChange={(event) =>
                update({ working: event.target.value as typeof params.working })
              }
              aria-label={t('airlines.columns.working')}
            />
          </label>

          <Button size="sm" onClick={reset} disabled={!hasFilters}>
            {t('airlines.resetFilters')}
          </Button>
        </div>

        {flash && (
          <p className={styles.flash} role="status">
            {flash}
          </p>
        )}

        {errorKey && (
          <p className={styles.error} role="alert">
            {t(errorKey)}
          </p>
        )}

        {missing && <p className={styles.warning}>{t('airlines.notFound')}</p>}

        <div className={styles.tableWrap}>
          {isLoading && <div className={styles.loadingBar} aria-hidden="true" />}

          <table className={cx(styles.table, isLoading && styles.loading)}>
            <thead>
              <tr className={styles.headRow}>
                {COLUMNS.map((column) => (
                  <SortHead
                    key={column.labelKey}
                    field={column.field}
                    labelKey={column.labelKey}
                    sort={params.sort}
                    dir={params.dir}
                    onSort={(field) =>
                      update({
                        sort: field,
                        dir:
                          params.sort === field && params.dir === 'ascending'
                            ? 'descending'
                            : 'ascending',
                      })
                    }
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={styles.message}>
                    {t(airlines.length === 0 ? 'common.noData' : 'airlines.nothingFound')}
                  </td>
                </tr>
              ) : (
                rows.map((airline) => (
                  <AirlineRow
                    key={airline.id}
                    airline={airline}
                    isOpen={selected?.id === airline.id}
                    onOpen={() => select({ mode: 'edit', id: airline.id })}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          rows={params.rows}
          total={matched.length}
          countLabel={t('airlines.found')}
          // Computed rather than read from a server: the whole dictionary is in hand,
          // so the last page is a fact rather than something to probe for.
          isLastPage={page >= pageCount}
          rowsOptions={ROWS_OPTIONS}
          onPage={(next) => update({ page: next })}
          onRows={(rowCount) => update({ rows: rowCount })}
          isLoading={isLoading}
        />
      </div>

      {/* Outside the page, because it is a dialog in the browser's top layer: the
          table never has to make room for it. */}
      {(isCreating || selected !== null) && (
        <AirlinePanel
          // Keyed by the row, so choosing another one rebuilds the form from that row
          // rather than an effect copying it over what is on screen.
          key={selected === null ? 'create' : String(selected.id)}
          airline={selected}
          canWrite={canWrite}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          onClose={() => select({ mode: 'closed' })}
        />
      )}
    </>
  );
};

/**
 * The columns, in order.
 *
 * A list rather than repeated markup: the head row and the "nothing found" colspan
 * have to agree on the count, and a list is the only version of that which cannot
 * drift. `null` marks a column with no sort of its own.
 */
const COLUMNS: ReadonlyArray<{ field: AirlineSortField | null; labelKey: Key }> = [
  { field: 'iataCode', labelKey: 'airlines.columns.iata' },
  { field: 'icaoCode', labelKey: 'airlines.columns.icao' },
  { field: 'title', labelKey: 'airlines.columns.name' },
  { field: 'prefix', labelKey: 'airlines.columns.prefix' },
  // Sorting two values into two blocks is what the filter beside it already does, and
  // it says which block you are looking at.
  { field: null, labelKey: 'airlines.columns.working' },
];

/**
 * What the search box looks at.
 *
 * The same fields `/airline/search` covers, matched here instead: the dictionary is
 * already in the browser, so a request per keystroke would buy nothing — and that
 * endpoint caps at 40 rows without saying so, which a table must not do.
 */
const matches = (airline: Airline, needle: string): boolean =>
  [airline.iataCode, icaoOf(airline), airline.prefix, airline.title, airline.fullTitle].some(
    (field) => typeof field === 'string' && field.toLowerCase().includes(needle),
  );

/**
 * Orders two airlines by one column.
 *
 * An empty value sorts LAST in both directions rather than as a value below every
 * other: a dictionary ordered by ICAO code should not open with the several hundred
 * carriers that have none.
 */
const compare = (left: Airline, right: Airline, field: AirlineSortField): number => {
  const [a, b] = [valueOf(left, field), valueOf(right, field)];

  if (a === '' || b === '') return a === b ? 0 : a === '' ? 1 : -1;
  return a.localeCompare(b);
};

const valueOf = (airline: Airline, field: AirlineSortField): string => {
  switch (field) {
    case 'iataCode':
      return airline.iataCode ?? '';
    // Trimmed, or the legacy space-padded rows sort as if they had a code.
    case 'icaoCode':
      return icaoOf(airline) ?? '';
    case 'title':
      return airline.title ?? '';
    case 'prefix':
      return airline.prefix ?? '';
  }
};

/**
 * A sortable column head.
 *
 * At module level, not inside AirlinesPage: a component created during a render is a
 * new type every time, so React unmounts and remounts its whole subtree — which in a
 * table means the search field above loses focus mid-word.
 */
const SortHead = ({
  field,
  labelKey,
  sort,
  dir,
  onSort,
}: {
  field: AirlineSortField | null;
  labelKey: Key;
  sort: AirlineSortField;
  dir: 'ascending' | 'descending';
  onSort: (field: AirlineSortField) => void;
}) => {
  const { t } = useTranslation();

  if (field === null) return <th>{t(labelKey)}</th>;

  const sorted = sort === field;

  return (
    <th aria-sort={sorted ? dir : 'none'}>
      <button
        type="button"
        className={cx(styles.sortButton, sorted && styles.sorted)}
        onClick={() => onSort(field)}
        title={t('airlines.sortHint')}
      >
        {t(labelKey)}
        <SortArrow
          sorted={sorted}
          dir={dir}
          className={styles.sortArrow}
          idleClassName={styles.sortIdle}
        />
      </button>
    </th>
  );
};

/** One carrier. */
const AirlineRow = ({
  airline,
  isOpen,
  onOpen,
}: {
  airline: Airline;
  isOpen: boolean;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();

  const icao = icaoOf(airline);
  const working = airline.kzFlying ?? false;

  /**
   * The whole row opens the card — a row is what the reader is aiming at, and a
   * two-character code is a very small target to have to hit.
   *
   * The click sits here and not on the code button, which keeps its `type` and its
   * focus ring but no handler of its own: a click on it, or Enter while it has focus,
   * dispatches a click that bubbles to this handler. One handler serves the mouse
   * anywhere on the row and the keyboard both; two would run twice for one gesture.
   *
   * The row gets no `tabIndex`: the button is already its tab stop and its accessible
   * name, and making the row focusable too would mean two stops per row.
   */
  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    // A drag across a cell ends in a click, so selecting a carrier's name to copy it
    // would otherwise open the card every time. `detail` is the click count, and it
    // is 0 for a click the keyboard synthesized — which cannot have come from a drag.
    if (event.detail > 0 && (window.getSelection()?.toString() ?? '') !== '') return;

    onOpen();
  };

  return (
    <tr
      className={cx(styles.row, isOpen && styles.selected, !working && styles.offListRow)}
      onClick={handleRowClick}
    >
      <td>
        {/* A button, not a link: the card opens in a dialog over the table rather
            than navigating away, so closing it leaves the reader on the same page of
            rows. */}
        <button type="button" className={styles.codeButton} aria-haspopup="dialog">
          {airline.iataCode ?? '—'}
        </button>
      </td>

      <td className={styles.mono}>{icao ?? <Empty />}</td>

      <td className={styles.wrap}>
        {airline.title ?? <Empty />}
        {/* The legal name under the short one: it has no column of its own, but it is
            what tells two similarly named carriers apart. */}
        {airline.fullTitle && airline.fullTitle !== airline.title && (
          <span className={styles.fullTitle}>{airline.fullTitle}</span>
        )}
      </td>

      <td className={styles.mono}>{airline.prefix ?? <Empty />}</td>

      <td>
        <Badge tone={working ? 'success' : 'neutral'}>
          {t(working ? 'airlines.states.working' : 'airlines.states.notWorking')}
        </Badge>
      </td>
    </tr>
  );
};

const Empty = () => <span className={styles.empty}>—</span>;
